import {
  Alert,
  Field,
  Input,
  Radio,
  RadioGroup,
  Switch,
} from '@billda/design-system';
import { UseFormReturn } from 'react-hook-form';
import { StepNavigation } from '../../molecules/StepNavigation';
import { GangMemberConfigCard } from '../../molecules/GangMemberConfigCard';
import { PermissionToggle } from '../../atoms/PermissionToggle';
import {
  GangCreationFormData,
  GangParticipant,
  PaymentType,
} from '../../../types/gangCreationTypes';

export interface GangMemberConfigurationStepProps {
  form: UseFormReturn<GangCreationFormData>;
  onNext: () => void;
  onBack: () => void;
  nextLabel: string;
}

const calculateTotalPercentage = (
  leaderIds: string[],
  memberIds: string[],
  participants: Record<string, GangParticipant>
) => {
  const ids = [...leaderIds, ...memberIds];
  return ids.reduce((total, userId) => {
    const participant = participants[userId];
    if (
      participant &&
      participant.paymentType === 'percentage' &&
      participant.defaultSplitPercentage !== null &&
      participant.defaultSplitPercentage !== undefined
    ) {
      return total + participant.defaultSplitPercentage;
    }
    return total;
  }, 0);
};

const hasAtLeastOnePercentageMember = (
  leaderIds: string[],
  memberIds: string[],
  participants: Record<string, GangParticipant>
) => {
  const ids = [...leaderIds, ...memberIds];
  return ids.some(userId => {
    const participant = participants[userId];
    return participant && participant.paymentType === 'percentage';
  });
};

const countPercentageMembers = (
  leaderIds: string[],
  memberIds: string[],
  participants: Record<string, GangParticipant>
): number => {
  const ids = [...leaderIds, ...memberIds];
  return ids.filter(userId => {
    const participant = participants[userId];
    return participant && participant.paymentType === 'percentage';
  }).length;
};

const calculateEqualPercentage = (percentageMemberCount: number): number => {
  if (percentageMemberCount === 0) {
    return 0;
  }
  // Round to 1 decimal place for display
  return Math.round((100 / percentageMemberCount) * 10) / 10;
};

export const GangMemberConfigurationStep = ({
  form,
  onNext,
  onBack,
  nextLabel,
}: GangMemberConfigurationStepProps) => {
  const leaderIds = form.watch('leaderIds') ?? [];
  const memberIds = form.watch('memberIds') ?? [];
  const participants = form.watch('participants') ?? {};
  const isEqualSplit = form.watch('isEqualSplit') ?? true;

  const leaderParticipants = leaderIds
    .map(userId => participants[userId])
    .filter((participant): participant is GangParticipant =>
      Boolean(participant)
    );
  const memberParticipants = memberIds
    .map(userId => participants[userId])
    .filter((participant): participant is GangParticipant =>
      Boolean(participant)
    );

  const percentageMemberCount = countPercentageMembers(
    leaderIds,
    memberIds,
    participants
  );

  const equalPercentage = calculateEqualPercentage(percentageMemberCount);

  // When isEqualSplit is true, calculate total from full precision (not rounded display)
  // This ensures 3 members shows 100.0% total even if individual displays show 33.3%
  const totalPercentage = isEqualSplit
    ? percentageMemberCount > 0
      ? 100
      : 0
    : calculateTotalPercentage(leaderIds, memberIds, participants);

  const hasPercentageMember = hasAtLeastOnePercentageMember(
    leaderIds,
    memberIds,
    participants
  );

  // Check if all fields have valid values
  const allFieldsValid = [...leaderParticipants, ...memberParticipants].every(
    participant => {
      if (participant.paymentType === 'percentage') {
        // When equal split is on, percentage members don't need a value
        if (isEqualSplit) {
          return true;
        }
        const percentage = participant.defaultSplitPercentage;
        return (
          percentage !== null &&
          percentage !== undefined &&
          percentage > 0 &&
          percentage <= 100
        );
      } else {
        // fixedRate
        const rate = participant.defaultRate;
        const units = participant.defaultUnitsValue;
        return (
          rate !== null &&
          rate !== undefined &&
          rate >= 0 &&
          units !== null &&
          units !== undefined &&
          units >= 0
        );
      }
    }
  );

  // When isEqualSplit is true, total is automatically 100%
  // When isEqualSplit is false, allow for 0.1% tolerance for 1dp rounding
  const totalIsValid = isEqualSplit || Math.abs(totalPercentage - 100) <= 0.1;

  const totalMembers = leaderParticipants.length + memberParticipants.length;

  const canNavigate =
    totalMembers > 0 && hasPercentageMember && totalIsValid && allFieldsValid;

  if (leaderParticipants.length === 0 && memberParticipants.length === 0) {
    return (
      <div className="p-6 text-center">
        <Alert severity="info" className="mb-4">
          No leaders or members were selected in previous steps. Please select
          at least one leader.
        </Alert>
        <StepNavigation
          onPrevious={onBack}
          onNext={() => {}}
          nextLabel="Next"
          disableNext
        />
      </div>
    );
  }

  const updateParticipant = (
    userId: string,
    updates: Partial<Omit<GangParticipant, 'userId'>>
  ) => {
    const currentParticipants = form.getValues('participants');
    const existing = currentParticipants[userId];
    if (!existing) {
      return;
    }

    const updated: GangParticipant = {
      ...existing,
      ...updates,
    };

    form.setValue(
      'participants',
      {
        ...currentParticipants,
        [userId]: updated,
      },
      { shouldDirty: true, shouldValidate: true }
    );
  };

  const handlePaymentTypeChange = (userId: string, value: PaymentType) => {
    const participant = participants[userId];
    if (!participant) {
      return;
    }

    // Clear fields based on payment type
    if (value === 'percentage') {
      updateParticipant(userId, {
        paymentType: value,
        defaultRate: null,
        defaultUnitsValue: null,
      });
    } else {
      updateParticipant(userId, {
        paymentType: value,
        defaultSplitPercentage: null,
      });
    }
  };

  const handlePercentageChange = (userId: string, value: string) => {
    const parsed =
      value === ''
        ? null
        : Number.isNaN(Number(value))
          ? null
          : Math.min(
              100,
              Math.round(parseFloat(value) * 10) / 10 // Round to 1 d.p., max 100
            );
    updateParticipant(userId, { defaultSplitPercentage: parsed });
  };

  const handleDefaultRateChange = (userId: string, value: string) => {
    const parsed =
      value === ''
        ? null
        : Number.isNaN(Number(value))
          ? null
          : Math.min(
              99999999.99,
              Math.round(parseFloat(value) * 100) / 100 // Round to 2 d.p.
            );
    updateParticipant(userId, { defaultRate: parsed });
  };

  const handleUnitsValueChange = (userId: string, value: string) => {
    const parsed =
      value === ''
        ? null
        : Number.isNaN(Number(value))
          ? null
          : Math.min(
              99999999.99,
              Math.round(parseFloat(value) * 100) / 100 // Round to 2 d.p.
            );
    updateParticipant(userId, { defaultUnitsValue: parsed });
  };

  const handleToggleSplitVisibility = (userId: string, checked: boolean) => {
    updateParticipant(userId, { canSeeSplit: checked });
  };

  const handleEqualSplitChange = (checked: boolean) => {
    form.setValue('isEqualSplit', checked, { shouldDirty: true });

    // When switching from equal to manual, pre-populate with current equal values
    if (!checked) {
      const currentParticipants = form.getValues('participants');
      const updated = { ...currentParticipants };
      const ids = [...leaderIds, ...memberIds];

      ids.forEach(userId => {
        const participant = updated[userId];
        if (participant && participant.paymentType === 'percentage') {
          updated[userId] = {
            ...participant,
            defaultSplitPercentage: equalPercentage,
          };
        }
      });

      form.setValue('participants', updated, { shouldDirty: true });
    }
  };

  const renderPaymentFields = (
    participant: GangParticipant,
    role: 'leader' | 'member'
  ) => {
    const testIdPrefix = role === 'leader' ? 'leader' : 'member';

    // Validation logic - show error for empty or invalid values
    const percentageValue = participant.defaultSplitPercentage;
    const hasPercentageError =
      percentageValue === null ||
      percentageValue === undefined ||
      percentageValue <= 0 ||
      percentageValue > 100;
    const percentageErrorText = hasPercentageError
      ? percentageValue === null || percentageValue === undefined
        ? 'Percentage is required'
        : 'Percentage must be between 0.01 and 100'
      : 'Total for all percentage members must equal 100%';

    const rateValue = participant.defaultRate;
    const hasRateError =
      rateValue === null || rateValue === undefined || rateValue < 0;
    const rateErrorText = hasRateError
      ? rateValue === null || rateValue === undefined
        ? 'Rate is required'
        : 'Rate must be 0 or greater'
      : 'Rate per unit';

    const unitsValue = participant.defaultUnitsValue;
    const hasUnitsError =
      unitsValue === null || unitsValue === undefined || unitsValue < 0;
    const unitsErrorText = hasUnitsError
      ? unitsValue === null || unitsValue === undefined
        ? 'Units are required'
        : 'Units must be 0 or greater'
      : 'e.g. 40 for 40 hours; 5 for 5 days';

    return (
      <>
        <fieldset className="mb-4 border-0 p-0">
          <legend className="mb-1.5 block type-label text-content-secondary">
            Payment Type
          </legend>
          <RadioGroup
            orientation="horizontal"
            value={participant.paymentType}
            onValueChange={value =>
              handlePaymentTypeChange(participant.userId, value as PaymentType)
            }
            data-testid={`${testIdPrefix}-payment-type-${participant.userId}`}
          >
            <label className="flex items-center gap-2 type-body-md">
              <Radio value="percentage" />
              Percentage Split
            </label>
            <label className="flex items-center gap-2 type-body-md">
              <Radio value="fixedRate" />
              Fixed Rate
            </label>
          </RadioGroup>
        </fieldset>

        {participant.paymentType === 'percentage' ? (
          <div className="flex-1">
            <Field
              label="Default Split Percentage"
              required={!isEqualSplit}
              hint={
                isEqualSplit
                  ? 'Automatically calculated from equal split'
                  : undefined
              }
              error={
                isEqualSplit || !hasPercentageError
                  ? undefined
                  : percentageErrorText
              }
            >
              <Input
                type="number"
                disabled={isEqualSplit}
                data-testid={`${testIdPrefix}-default-split-input`}
                min={0.1}
                max={100}
                step={0.1}
                value={
                  isEqualSplit
                    ? equalPercentage
                    : (participant.defaultSplitPercentage ?? '')
                }
                onChange={event =>
                  handlePercentageChange(participant.userId, event.target.value)
                }
              />
            </Field>
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex-1">
              <Field
                label="Rate (£)"
                required
                hint={!hasRateError ? rateErrorText : undefined}
                error={hasRateError ? rateErrorText : undefined}
              >
                <Input
                  type="number"
                  data-testid={`${testIdPrefix}-default-rate-input`}
                  min={0}
                  max={99999999.99}
                  step={0.01}
                  value={participant.defaultRate ?? ''}
                  onChange={event =>
                    handleDefaultRateChange(
                      participant.userId,
                      event.target.value
                    )
                  }
                />
              </Field>
            </div>
            <div className="flex-1">
              <Field
                label="Units"
                required
                hint={!hasUnitsError ? unitsErrorText : undefined}
                error={hasUnitsError ? unitsErrorText : undefined}
              >
                <Input
                  type="number"
                  data-testid={`${testIdPrefix}-default-units-input`}
                  min={0}
                  max={99999999.99}
                  step={0.01}
                  value={participant.defaultUnitsValue ?? ''}
                  onChange={event =>
                    handleUnitsValueChange(
                      participant.userId,
                      event.target.value
                    )
                  }
                />
              </Field>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="p-1">
      <h6 className="type-h6 mb-2">Configure Team Settings</h6>
      <p className="type-body-md mb-4 text-content-secondary">
        Set payment configuration for leaders and members. At least one member
        must use percentage split, and all percentage splits must total exactly
        100%.
      </p>

      {/* Equal Split Toggle */}
      <div className="mb-6 rounded-lg bg-surface-default p-4">
        <label className="flex items-center gap-2 type-body-md">
          <Switch
            checked={isEqualSplit}
            onCheckedChange={checked =>
              handleEqualSplitChange(checked === true)
            }
            data-testid="equal-split-toggle"
          />
          Split Equally
        </label>
        <p className="type-body-md ml-6 text-content-secondary">
          {isEqualSplit
            ? `All percentage members will receive ${equalPercentage}% each (${percentageMemberCount} member${percentageMemberCount !== 1 ? 's' : ''})`
            : 'Turn on to automatically distribute equal shares to percentage members'}
        </p>
      </div>

      {leaderParticipants.length > 0 && (
        <div className="mb-8">
          <h6 className="type-h6 mb-2 text-content-accent">
            Leader Configuration
          </h6>
          <p className="type-body-md mb-4 text-content-secondary">
            Leaders automatically have access to financial split information.
          </p>

          {leaderParticipants.map(leader => (
            <GangMemberConfigCard
              key={leader.userId}
              fullName={leader.fullName}
              email={leader.email}
              role="leader"
            >
              {renderPaymentFields(leader, 'leader')}
            </GangMemberConfigCard>
          ))}
        </div>
      )}

      {memberParticipants.length > 0 && (
        <div className="mb-8">
          <h6 className="type-h6 mb-2 text-content-accent">
            Member Configuration
          </h6>
          <p className="type-body-md mb-4 text-content-secondary">
            Configure each member&apos;s permissions and payment settings.
          </p>

          {memberParticipants.map(member => (
            <GangMemberConfigCard
              key={member.userId}
              fullName={member.fullName}
              email={member.email}
              role="member"
            >
              <div className="mb-4">
                <PermissionToggle
                  label="Can see financial split information"
                  checked={Boolean(member.canSeeSplit)}
                  onChange={checked =>
                    handleToggleSplitVisibility(member.userId, checked)
                  }
                  testIdSuffix={`${member.userId}`}
                />
              </div>

              {renderPaymentFields(member, 'member')}
            </GangMemberConfigCard>
          ))}
        </div>
      )}

      {!hasPercentageMember && (
        <Alert severity="error" className="mb-6">
          At least one member must use percentage split payment type.
        </Alert>
      )}

      {hasPercentageMember && !isEqualSplit && !totalIsValid && (
        <Alert severity="error" className="mb-6">
          The percentage split must total exactly 100% (currently{' '}
          {totalPercentage.toFixed(1)}%).
        </Alert>
      )}

      <StepNavigation
        onPrevious={onBack}
        onNext={onNext}
        nextLabel={nextLabel}
        disableNext={!canNavigate}
      />
    </div>
  );
};
